import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import AddIcon from "@mui/icons-material/Add";
import DescriptionIcon from "@mui/icons-material/Description";
import PlaceIcon from "@mui/icons-material/Place";
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Link,
} from "@mui/material";
import { useSessionStorage } from "@react-hooks-library/core";
import { SubmissionSummary } from "@rubi/api-generated";
import { GoogleMapsProvider } from "@rubi/ui";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";

import { getServiceConfig } from "@/common/serviceConfigurations";
import { mailTo } from "@/common/utils";
import SearchBar, {
  SearchSuggestion,
  SearchSuggestionGroup,
} from "@/components/alerts/Search/SearchBar";
import { DisabledComponentDialog } from "@/components/DisabledComponent";
import { OnlineReportingContext } from "@/components/online-reporting/context";
import ReportFilters, {
  IReportListFilterOptions,
  RenderReportFilterChips,
} from "@/components/online-reporting/Search/ReportFilters";
import {
  DEFAULT_SUBMISSION_SORT,
  SubmissionSort,
  sortFromFilters,
} from "@/components/online-reporting/submissionSort";
import { getTitleFromSubmission } from "@/components/online-reporting/utils";
import { parseUserPermissions } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useDevEnvironment } from "@/contexts/DevContext";
import {
  PLACE_QUERY_MIN_LENGTH,
  usePlacePredictions,
} from "@/hooks/usePlacePredictions";
import { useUserLocation } from "@/hooks/useUserLocation";
import { IUserPermission } from "@/types";

import { useRegistry } from "./context/RegistryContext";
import RegistryCard from "./RegistryCard";
import RegistryDetailsPanel from "./RegistryDetailsPanel";
import RegistryListView from "./RegistryListView";
import RegistryMapView, { FocusedEntry, FocusedPlace } from "./RegistryMapView";

const REGISTRY_VIEW_KEY = "registryView";
const ENTRY_SUGGESTION_GROUP = "registry-entries";
const PLACE_SUGGESTION_GROUP = "places";
const MAX_ENTRY_SUGGESTIONS = 5;

// `useSessionStorage` initializes to its default and only hydrates the stored
// value in a post-mount effect. That briefly flips `view` from the default
// ("map") to the stored value, which re-runs the view-change effect below and
// resets the table page (defeating the page-memory restore). Reading the
// stored view synchronously makes `view` correct on the first render, so that
// phantom transition never happens.
const getStoredRegistryView = (): "card" | "list" | "map" => {
  try {
    const raw = sessionStorage.getItem(REGISTRY_VIEW_KEY);
    return raw ? JSON.parse(raw) : "map";
  } catch {
    return "map";
  }
};

const RegistryEntriesContent = () => {
  const { orgId } = useParams();
  const { t } = useTranslation("bulletin/searchbar");
  const { registryForms, registryTablePage, setRegistryTablePage } =
    useRegistry();
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [newEntryModalOpen, setNewEntryModalOpen] = useState(false);
  const { isDevEnv } = useDevEnvironment();
  const {
    fetchLatestReports,
    reports,
    searchText,
    setSearchText,
    fetchReportConfigurations,
    registryTypeNames,
    totalReportsCount,
    rv2Users,
    fetchRv2Users,
  } = useContext(OnlineReportingContext);
  const [registrySubmissions, setRegistrySubmissions] = useState<
    SubmissionSummary[]
  >([]);
  const [view, setView] = useSessionStorage(
    REGISTRY_VIEW_KEY,
    getStoredRegistryView(),
  );

  const setSearchView = (view: "card" | "list" | "map") => {
    if (view !== "map") {
      setPaginationModel((prev) => ({ page: 0, pageSize: prev.pageSize }));
    }
    setView(view);
  };

  const navigate = useNavigate();
  const { currentUser } = useContext(AuthStateContext);
  const [reportPermissions] = useState(
    currentUser &&
      parseUserPermissions(currentUser?.permissions as IUserPermission[]),
  );
  const canViewEntriesRegistry = reportPermissions
    ? reportPermissions.canViewEntriesRegistry
    : true;

  // Simplified filters for registry entries
  const [filters, setFilters] = useSessionStorage<IReportListFilterOptions>(
    "registryFilters",
    {
      creationStartDate: null,
      creationEndDate: null,
      updateStartDate: null,
      updateEndDate: null,
      reportStatus: null,
      trackingNumber: null,
      reportType: null,
      assignedTo: null,
      sortOrder: DEFAULT_SUBMISSION_SORT.sortOrder,
      sortBy: DEFAULT_SUBMISSION_SORT.sortBy,
    },
  );
  const sort = sortFromFilters(filters);

  const [paginationModel, setPaginationModel] = useState({
    page: registryTablePage ?? 0,
    pageSize: 25, // Initial value, will be adjusted by autoPageSize
  });

  useEffect(() => {
    setPaginationModel((prev) => ({
      ...prev,
      page: registryTablePage ?? 0,
    }));
  }, [registryTablePage]);

  const handlePaginationChange = (params: {
    page: number;
    pageSize: number;
  }) => {
    setPaginationModel(params);
    setRegistryTablePage(params.page);
  };

  const searchBarRef = useRef<HTMLInputElement | null>(null);

  const updateSearch = (updatedFilters: IReportListFilterOptions) => {
    setFilters(updatedFilters);
  };

  const handleSortChange = (next: SubmissionSort) => {
    setRegistryTablePage(0);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setFilters({ ...filters, ...next });
  };

  const [debouncedFilters, setDebouncedFilters] =
    useState<IReportListFilterOptions | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [filters]);

  // useEffect(() => {
  //   if (registryTypeNames) {
  //     setFilters({
  //       ...filters,
  //       reportType: registryTypeNames.join("|"),
  //     });
  //   }
  // }, [registryTypeNames]);

  // useEffect(() => {
  //   if (registryForms) {
  //     const registry = registryForms.find(
  //       (survey) => survey.survey_class === registryName,
  //     );
  //     if (registry) {
  //       setRegistryTitle(registry.name);
  //     }
  //   }
  // }, [registryForms, registryName]);

  useEffect(() => {
    async function fetchData() {
      setLoadingEntries(true);
      if (!initialized) {
        await fetchReportConfigurations(orgId ?? "");
        setInitialized(true);
      }
      try {
        await fetchLatestReports!(
          orgId ?? "",
          debouncedFilters?.creationStartDate ?? undefined,
          debouncedFilters?.creationEndDate ?? undefined,
          debouncedFilters?.updateStartDate ?? undefined,
          debouncedFilters?.updateEndDate ?? undefined,
          debouncedFilters?.reportStatus
            ? decodeURIComponent(debouncedFilters?.reportStatus)
            : undefined,
          debouncedFilters?.trackingNumber ?? undefined,
          paginationModel.pageSize > 0 ? paginationModel.pageSize : undefined,
          paginationModel.page * paginationModel.pageSize,
          debouncedFilters?.reportType
            ? decodeURIComponent(debouncedFilters?.reportType)
            : undefined,
          searchText,
          debouncedFilters?.assignedTo ?? undefined,
          true,
          undefined,
          undefined,
          debouncedFilters?.sortOrder,
          undefined,
          debouncedFilters?.sortBy,
        );
        setLoadingEntries(false);
      } catch (e: any) {
        setLoadingEntries(false);
        const message = e.message;
        toast.error(
          `Failed to fetch registry entries${isDevEnv ? `: ${message}` : ""}`,
        );
      }
    }

    if (debouncedFilters) {
      fetchData();
    }
  }, [debouncedFilters, searchText, initialized, paginationModel]);

  useEffect(() => {
    setRegistrySubmissions(
      reports.filter((report) =>
        registryTypeNames.includes(report.survey_name ?? ""),
      ),
    );
  }, [reports, registryTypeNames]);

  const renderFilters = () => {
    return (
      <ReportFilters
        isOpen={filtersVisible}
        anchorEl={searchBarRef.current}
        onClose={() => setFiltersVisible(false)}
        filters={filters}
        setFilters={updateSearch}
        registryFilters={true}
      />
    );
  };

  const [selectedEntry, setSelectedEntry] = useState<
    SubmissionSummary | undefined
  >();
  const [detailsPanelOpen, setDetailsPanelOpen] = useState(false);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<
    string | undefined
  >();

  const [focusedEntry, setFocusedEntry] = useState<FocusedEntry | null>(null);

  const handleEntryClick = (entry: SubmissionSummary) => {
    setSelectedEntry(entry);
    setSelectedSubmissionId(entry.id);
    setDetailsPanelOpen(true);
  };

  const focusEntryOnMap = (entry: SubmissionSummary) => {
    handleEntryClick(entry);
    setFocusedEntry((previous) => ({
      id: entry.id,
      token: (previous?.token ?? 0) + 1,
    }));
  };

  const [draftQuery, setDraftQuery] = useState("");
  const [focusedPlace, setFocusedPlace] = useState<FocusedPlace | null>(null);
  const [resultFocusToken, setResultFocusToken] = useState<number>();
  const [mapBounds, setMapBounds] =
    useState<google.maps.LatLngBoundsLiteral | null>(null);

  const handleSearchCommit = () => {
    setFocusedPlace(null);
    setResultFocusToken((token) => (token ?? 0) + 1);
  };

  const handleViewportChange = useCallback(
    (bounds: google.maps.LatLngBoundsLiteral) =>
      setMapBounds((previous) =>
        previous &&
        previous.north === bounds.north &&
        previous.south === bounds.south &&
        previous.east === bounds.east &&
        previous.west === bounds.west
          ? previous
          : bounds,
      ),
    [],
  );

  const mapCenter = getServiceConfig(
    orgId ?? currentUser?.rubi_org_id ?? "NA",
    isDevEnv,
  ).mapCenter;

  const userLocation = useUserLocation(
    draftQuery.trim().length >= PLACE_QUERY_MIN_LENGTH,
  );

  const {
    predictions,
    loading: placesLoading,
    resolvePlace,
  } = usePlacePredictions(draftQuery, {
    center: userLocation ?? mapCenter,
    bounds: mapBounds ?? undefined,
  });

  const entrySuggestions = useMemo(() => {
    const needle = draftQuery.trim().toLowerCase();
    if (!needle) return [];

    return registrySubmissions
      .map((entry) => {
        const title =
          getTitleFromSubmission(
            {
              submission: entry.latest_data,
              first_name: entry.first_name,
              last_name: entry.last_name,
            },
            entry.survey_class ?? "",
          ) || `${entry.first_name ?? ""} ${entry.last_name ?? ""}`.trim();

        const registryTitle =
          registryForms?.find(
            (form) => form.survey_class === entry.survey_class,
          )?.name ?? entry.survey_name;

        return {
          entry,
          title: title || registryTitle || "Registry entry",
          registryTitle,
        };
      })
      .filter(({ entry, title, registryTitle }) =>
        [title, registryTitle, entry.tracking_number].some((field) =>
          field?.toString().toLowerCase().includes(needle),
        ),
      )
      .slice(0, MAX_ENTRY_SUGGESTIONS)
      .map(({ entry, title, registryTitle }) => ({
        id: entry.id,
        groupKey: ENTRY_SUGGESTION_GROUP,
        primaryText: title,
        secondaryText: [registryTitle, entry.tracking_number]
          .filter(Boolean)
          .join(" · "),
        icon: <DescriptionIcon fontSize="small" sx={{ color: "#5F6368" }} />,
      }));
  }, [draftQuery, registrySubmissions, registryForms]);

  const suggestionGroups: SearchSuggestionGroup[] = [
    {
      key: ENTRY_SUGGESTION_GROUP,
      label: t("suggestionsRegistryEntries"),
      suggestions: entrySuggestions,
      searchAllLabel: t("suggestionsSearchAll"),
    },
    {
      key: PLACE_SUGGESTION_GROUP,
      label: t("suggestionsPlaces"),
      suggestions: predictions.map((prediction) => ({
        id: prediction.placeId,
        groupKey: PLACE_SUGGESTION_GROUP,
        primaryText: prediction.primaryText,
        secondaryText: prediction.secondaryText,
        icon: <PlaceIcon fontSize="small" sx={{ color: "#5F6368" }} />,
      })),
    },
  ];

  const handleSuggestionSelect = async (suggestion: SearchSuggestion) => {
    if (suggestion.groupKey === ENTRY_SUGGESTION_GROUP) {
      const entry = registrySubmissions.find(
        (item) => item.id === suggestion.id,
      );
      if (!entry) return;
      setFocusedPlace(null);
      focusEntryOnMap(entry);
      return;
    }

    const place = await resolvePlace(suggestion.id);
    if (!place) {
      toast.error(t("suggestionPlaceError"));
      return;
    }

    if (view !== "map") setSearchView("map");
    setFocusedPlace(place);
  };

  const bottomPageRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          console.log(
            entry.isIntersecting,
            loadingEntries,
            reports.length,
            totalReportsCount,
          );
          if (
            entry.isIntersecting &&
            !loadingEntries &&
            reports.length < totalReportsCount
          ) {
            setPaginationModel((prev) => ({
              page: 0,
              pageSize: prev.pageSize + 8,
            }));
          }
        });
      },
      { threshold: 1 },
    );

    const currentAnchor = bottomPageRef.current;
    if (currentAnchor) {
      observer.observe(currentAnchor);
    }

    return () => {
      if (currentAnchor) {
        observer.unobserve(currentAnchor);
      }
    };
  }, [bottomPageRef, loadingEntries, view]);

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // On initial mount, just set pageSize for map without resetting page
      if (view === "map") {
        setPaginationModel((prev) => ({ ...prev, pageSize: 10000 }));
      }
      return;
    }
    if (view === "map") {
      setPaginationModel({ page: 0, pageSize: 10000 });
    }
    // For list view, let autoPageSize handle the pageSize calculation
    if (view === "list") {
      setPaginationModel((prev) => ({ page: 0, pageSize: prev.pageSize }));
    }
  }, [view]);

  // Add useEffect to fetch users for assignment functionality
  useEffect(() => {
    if (orgId && rv2Users === null) {
      fetchRv2Users(orgId);
    }
  }, [orgId, fetchRv2Users]);

  const NewEntryModal = () => {
    const [selectedRegistry, setSelectedRegistry] = useState("");

    const handleConfirm = () => {
      if (selectedRegistry) {
        const form = registryForms.find(
          (form) => form.name === selectedRegistry,
        );
        window.location.href = `/rubiregistry/${orgId}/${form?.survey_class}/new`;
      }
    };

    return (
      <Dialog
        open={newEntryModalOpen}
        onClose={() => setNewEntryModalOpen(false)}
      >
        <DialogTitle>Select Registry Type</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Registry Type</InputLabel>
            <Select
              value={selectedRegistry}
              onChange={(e) => setSelectedRegistry(e.target.value)}
              label="Registry Type"
            >
              {registryTypeNames.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewEntryModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={!selectedRegistry}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  if (!canViewEntriesRegistry) {
    return (
      <DisabledComponentDialog
        isDialogOpen={true}
        handleClose={() => {
          navigate(`/`);
        }}
        title="You do not have access to view registry entries."
        message={
          <Typography>
            Please contact{" "}
            <Link
              rel="noopener noreferrer"
              target="_blank"
              href={mailTo({
                email: "support@tryrubicon.com",
                subject: "Requesting Access to View Reports",
                body: `Hello Rubicon Support Team, 

                I would like to request access to view registry entries.

                Thank you. 

                Sincerely,

                ${currentUser?.first_name} ${currentUser?.last_name} 
                ${currentUser?.email}`,
              })}
              color="secondary"
            >
              support@tryrubicon.com
            </Link>{" "}
            to request access.
          </Typography>
        }
      />
    );
  }

  return (
    <div style={{ padding: "8px 16px", display: "relative" }}>
      <NewEntryModal />
      <div
        style={{
          position: "absolute",
          right: "16px",
          top: "16px",
          width: "100px",
          zIndex: 10,
          alignItems: "center",
        }}
      >
        <Button
          onClick={() => setNewEntryModalOpen(true)}
          variant="contained"
          startIcon={<AddIcon />}
        >
          New
        </Button>
      </div>
      <SearchBar
        getMoreItems={() => {}}
        onSearch={handleSearchCommit}
        onClearSearch={() => setFocusedPlace(null)}
        searchView={view}
        setSearchView={setSearchView}
        defaultView="map"
        searchText={searchText}
        setSearchText={setSearchText}
        currentItems={reports}
        setCurrentItems={() => {}}
        filtersVisible={filtersVisible}
        setFiltersVisible={setFiltersVisible}
        totalAlerts={reports.length}
        loading={loadingEntries}
        setLoading={setLoadingEntries}
        searchType="report"
        filtersRef={searchBarRef}
        filterChips={
          <RenderReportFilterChips
            filters={filters}
            setFilters={updateSearch}
          />
        }
        activeViews={["list", "card", "map"]}
        suggestionGroups={suggestionGroups}
        suggestionsLoading={placesLoading}
        onSuggestionSelect={handleSuggestionSelect}
        onDraftChange={setDraftQuery}
        noSuggestionsText={t("suggestionsEmpty")}
        sort={sort}
        onSortChange={handleSortChange}
      />
      {filtersVisible && renderFilters()}
      {view === "list" && (
        <div
          style={{
            height: "calc(100vh - 206px)",
            width: "100%",
            marginTop: "16px",
          }}
        >
          <RegistryListView
            reports={registrySubmissions}
            loadingEntries={loadingEntries}
            totalReportsCount={totalReportsCount}
            paginationModel={paginationModel}
            handlePaginationChange={handlePaginationChange}
            sort={sort}
            onSortChange={handleSortChange}
          />
        </div>
      )}
      {view === "card" && (
        <div
          style={{
            height: "calc(100vh - 206px)",
            width: "100%",
            marginTop: "16px",
            overflowY: "auto",
          }}
        >
          <Grid container spacing={2}>
            {registrySubmissions.map((report, index) => (
              <Grid
                size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                key={report.id || index}
                sx={{
                  display: "flex",
                  "& > *": {
                    // This targets the RegistryCard component
                    flex: 1,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    "& .MuiCardContent-root": {
                      // Target the card content
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-end", // Push content to bottom
                    },
                  },
                }}
              >
                <RegistryCard
                  index={index}
                  registryItem={report}
                  onCardClick={handleEntryClick}
                />
              </Grid>
            ))}
          </Grid>
          <div
            className="bottom-page-ref"
            ref={bottomPageRef}
            style={{ height: "10px" }}
          ></div>
          {loadingEntries && <CircularProgress />}
        </div>
      )}
      {view === "map" && (
        <div
          style={{
            height: "calc(100vh - 206px)",
            width: "100%",
            marginTop: "16px",
          }}
        >
          <RegistryMapView
            reports={registrySubmissions}
            loadingEntries={loadingEntries}
            onPinClick={focusEntryOnMap}
            selectedReportId={selectedSubmissionId}
            mapCenter={mapCenter}
            focusedPlace={focusedPlace}
            focusedEntry={focusedEntry}
            onViewportChange={handleViewportChange}
            focusResultsToken={resultFocusToken}
          />
        </div>
      )}
      <RegistryDetailsPanel
        open={detailsPanelOpen}
        onClose={() => {
          setDetailsPanelOpen(false);
          setSelectedSubmissionId(undefined);
        }}
        registryItem={selectedEntry}
      />
    </div>
  );
};

const RegistryEntries = () => (
  <GoogleMapsProvider>
    <RegistryEntriesContent />
  </GoogleMapsProvider>
);

export default RegistryEntries;
